import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINRFull, formatCr } from "@/lib/format";
import {
  ArrowLeft, Building2, Calendar, Percent, Wallet, Shield, Tag, Clock,
  MapPin, Receipt, TrendingDown, Landmark, Factory,
} from "lucide-react";

const BUCKET_COLORS = {
  "On time": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "0-30 DPD": "bg-lime-50 text-lime-700 border-lime-200",
  "31-60 DPD": "bg-amber-50 text-amber-700 border-amber-200",
  "61-90 DPD": "bg-orange-50 text-orange-700 border-orange-200",
  "91-365 DPD": "bg-red-50 text-[#b3122a] border-red-200",
  "Closed": "bg-slate-100 text-slate-500 border-slate-200",
};

function Row({ icon: Icon, label, value, danger, emphasis }) {
  const color = danger ? "text-[#b3122a]"
    : emphasis === "rate" ? "text-[#0e7490]"
    : emphasis === "principal" ? "text-[#b3122a]"
    : emphasis ? "text-[#111111]" : "text-[#1a1a1b]";
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2.5 text-slate-500">
        <Icon size={17} className="text-slate-400" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className={`text-right tabular-nums ${emphasis ? "text-base font-extrabold" : "text-sm font-semibold"} ${color}`}>{value || "—"}</span>
    </div>
  );
}

export default function LoanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/portfolio/loans/${id}`).then((r) => setLoan(r.data)).catch((e) => setError(e.response?.data?.detail || e.message));
  }, [id]);

  if (error) return <div className="p-8 text-[#b3122a]" data-testid="loan-detail-error">{error}</div>;
  if (!loan) return <div className="p-8 text-slate-400" data-testid="loan-detail-loading">Loading…</div>;

  const repaidPct = loan.sanction_amount ? Math.min(100, Math.round((1 - loan.outstanding_principal / loan.sanction_amount) * 100)) : 0;
  const totalOverdue = loan.principal_overdue + loan.interest_overdue;

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="loan-detail-page">
      <button onClick={() => navigate("/portfolio")} data-testid="back-button" className="flex items-center gap-2 text-slate-500 hover:text-[#b3122a] transition mb-5 text-sm font-semibold">
        <ArrowLeft size={18} /> Back to Loan Book
      </button>

      <div className="rf-card p-6 mb-5 border-t-4 border-t-[#111111]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-mono text-xs text-slate-400 mb-1">{loan.facility}</div>
            <h1 className="font-display text-3xl font-extrabold text-[#1a1a1b]">{loan.borrower}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${BUCKET_COLORS[loan.bucket] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{loan.bucket}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-600">{loan.product}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-600">{loan.geography}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-500">Total Outstanding</div>
            <div className="font-display text-3xl font-extrabold text-[#b3122a]">{formatINRFull(loan.total_outstanding)}</div>
            <div className="text-xs text-slate-400 mt-1">Sanctioned: {formatINRFull(loan.sanction_amount)}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-slate-500 font-medium">Principal repaid</span>
            <span className="font-semibold text-[#1a1a1b]">{repaidPct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#0ca678] rounded-full transition-all" style={{ width: `${repaidPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-slate-400 mt-1.5">
            <span>O/s principal: {formatINRFull(loan.outstanding_principal)}</span>
            <span>Tenor left: {loan.outstanding_tenor} of {loan.tenure} mo</span>
          </div>
        </div>
      </div>

      {/* Overdue alert */}
      {totalOverdue > 0 && (
        <div className="rf-card p-4 mb-5 border-l-4 border-l-[#b3122a] bg-[#b3122a]/[0.03]" data-testid="overdue-alert">
          <div className="flex items-center gap-3">
            <TrendingDown size={20} className="text-[#b3122a]" />
            <div>
              <div className="font-semibold text-[#1a1a1b]">Overdue: {formatINRFull(totalOverdue)}</div>
              <div className="text-xs text-slate-500">Principal {formatINRFull(loan.principal_overdue)} · Interest {formatINRFull(loan.interest_overdue)} · Bucket {loan.bucket}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rf-card p-6">
          <h3 className="font-display font-bold text-[#1a1a1b] mb-3">Facility Terms</h3>
          <Row icon={Wallet} label="Sanctioned" value={formatINRFull(loan.sanction_amount)} emphasis />
          <Row icon={Wallet} label="Total Outstanding" value={formatINRFull(loan.total_outstanding)} emphasis="principal" />
          <Row icon={Wallet} label="Outstanding Principal" value={formatINRFull(loan.outstanding_principal)} emphasis="principal" />
          <Row icon={Percent} label="Interest Rate (ROI)" value={`${loan.roi}%`} emphasis="rate" />
          <Row icon={Shield} label="LTV" value={`${(loan.ltv * 100).toFixed(0)}%`} />
          <Row icon={Clock} label="Tenure" value={`${loan.tenure} months`} />
          <Row icon={Receipt} label="Monthly EMI" value={formatINRFull(loan.monthly_emi)} />
        </div>

        <div className="rf-card p-6">
          <h3 className="font-display font-bold text-[#1a1a1b] mb-3">Dues & Recovery</h3>
          <Row icon={Wallet} label="Principal Due" value={formatINRFull(loan.principal_due)} />
          <Row icon={TrendingDown} label="Principal Overdue" value={formatINRFull(loan.principal_overdue)} danger={loan.principal_overdue > 0} />
          <Row icon={Wallet} label="Interest Due" value={formatINRFull(loan.interest_due)} />
          <Row icon={TrendingDown} label="Interest Overdue" value={formatINRFull(loan.interest_overdue)} danger={loan.interest_overdue > 0} />
          <Row icon={Receipt} label="Principal Recovered" value={formatINRFull(loan.principal_received_current + loan.principal_received_overdue)} />
          <Row icon={Receipt} label="Prepayment" value={formatINRFull(loan.prepayment)} />
        </div>

        <div className="rf-card p-6">
          <h3 className="font-display font-bold text-[#1a1a1b] mb-3">Profile</h3>
          <Row icon={Building2} label="Product" value={loan.product} />
          <Row icon={Factory} label="Industry" value={loan.industry} />
          <Row icon={Tag} label="Nature" value={loan.nature} />
          <Row icon={MapPin} label="Geography" value={`${loan.geography}${loan.district ? ` · ${loan.district}` : ""}`} />
        </div>

        <div className="rf-card p-6">
          <h3 className="font-display font-bold text-[#1a1a1b] mb-3">Collateral & Timeline</h3>
          <Row icon={Shield} label="Collateral Value" value={formatINRFull(loan.collateral_value)} />
          <Row icon={Landmark} label="Collateral" value={loan.collateral_type} />
          <Row icon={Calendar} label="Sanction Date" value={loan.date_of_sanction} />
          <Row icon={Calendar} label="Disbursement Date" value={loan.date_of_disbursement} />
          <Row icon={Calendar} label="Maturity" value={loan.end_date} />
        </div>
      </div>
    </div>
  );
}
